import { LocalizationModuleError } from "../errors/LocalizationModuleError";
import { LocalizationModuleErrorConstants } from "../errors/LocalizationModuleErrorConstants";
import { ILocalizeService } from "../service/api/ILocalizeService";
import { TimeType } from "./time/TimeType";
import { HTMLTextVO } from "../vos/HTMLTextVO";
import { LocalizedTimeFormatVO } from "../vos/LocalizedTimeFormatVO";
import { DateTimeStyle } from "../globalization/DateTimeStyle";

import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("LocalizationJS.Localize");

/**
 * <p>
 *     <strong>Localize</strong>
 * </p>
 *
 * <p>
 *     The <code>Localize</code> class is a wrapper for the <code>ILocalizeService</code> interface and is
 *     intended to be used when there is not possibility to get the localization setService through injection.
 *     This class only expose all methods from the interface, allowing direct access to them.
 * </p>
 *
 * <p>
 *     Before use this class, have sure that the module was already installed and configured properly into game
 *     context. Otherwise this class will throw a error.
 * </p>
 *
 * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService
 *
 */
export class Localize {
    public static CODE_LOCALIZE_SERVICE_NOT_ASSIGNED: string = "001";
    public static MESSAGE_LOCALIZE_SERVICE_NOT_ASSIGNED: string =
        "Localize Service was not assigned to Static Localize Helper (use setService to do so)";

    /**
     * @private
     */
    private static _service: ILocalizeService;

    /**
     * @private
     * @param localizeService
     */
    public static setService(localizeService: ILocalizeService): void {
        if (Localize._service != null) {
            logger.warn("LocalizeService was already setup - it is going to be overridden!");
        }
        Localize._service = localizeService;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#text()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#text()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static text(textId: string, replacements: any = null, useNumberGrouping: boolean = true): string {
        Localize.assertModuleIsInstalled();

        return Localize._service.text(textId, replacements, useNumberGrouping);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#hasText()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#hasText()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static hasText(textId: string): boolean {
        Localize.assertModuleIsInstalled();

        return Localize._service.hasText(textId);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#number()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#number()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static number(
        numberValue: number,
        compact: boolean = false,
        fractionalDigits: number = -1,
        isRTL: boolean = false,
        useGrouping: boolean = true
    ): string {
        Localize.assertModuleIsInstalled();

        return Localize._service.number(numberValue, compact, fractionalDigits, isRTL, useGrouping);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#integer()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#integer()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static integer(textId: number): string {
        Localize.assertModuleIsInstalled();
        return Localize._service.integer(textId);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#decimal()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#decimal()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static decimal(textId: number): string {
        Localize.assertModuleIsInstalled();
        return Localize._service.decimal(textId);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#datetime()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#datetime()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static datetime(date: Date, dateStyle: string = DateTimeStyle.SHORT, timeStyle: string = DateTimeStyle.SHORT): string {
        Localize.assertModuleIsInstalled();

        return Localize._service.datetime(date, dateStyle, timeStyle);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#time()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#time()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static time(
        seconds: number,
        trim: number = 2,
        leadingZero: boolean = true,
        localizedTimeFormatVO: LocalizedTimeFormatVO = null
    ): string {
        Localize.assertModuleIsInstalled();

        return Localize._service.time(seconds, trim, leadingZero, localizedTimeFormatVO);
    }


    public static digitalClock(time:string) :string {
        return Localize._service.digitalClock(time);
    }

    /**
     * For some reason games are using NumberVO to display simple numbers as string without localization (decimal points etc ), otherwise LocalizeNumber is used.
     * To avoid usage of Latin numbers now that Globalize handles Arabic numbers ( Flash did not) and to avoid refactor large parts of games, we expose this method, which will localize numbers only if current locale is arabic
     * @param number
     */
    public static numberForArabic(value:number):string {
        return Localize._service.numberForArabic(value);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#timeCustom()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#timeCustom()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static timeCustom(format: string, seconds: number, roundTo: TimeType = null): string {
        Localize.assertModuleIsInstalled();

        return Localize._service.timeCustom(format, seconds, roundTo);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizeService#htmlText()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizeService#htmlText()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static htmlText(htmlTextVO: HTMLTextVO): string {
        Localize.assertModuleIsInstalled();
        return Localize._service.htmlText(htmlTextVO);
    }

    public static updateCurrencyFormatter(code: string, symbol: string): void {
        Localize._service.updateCurrencyFormatter(code, symbol);
    }

    public static formatCurrency(value: number, withSymbol = false): string {
        return Localize._service.formatCurrency(value, withSymbol);
    }

    /**
     * @private
     */
    private static assertModuleIsInstalled(): void {
        if (Localize._service == null) {
            throw new LocalizationModuleError(Localize.MESSAGE_LOCALIZE_SERVICE_NOT_ASSIGNED, Localize.CODE_LOCALIZE_SERVICE_NOT_ASSIGNED);
        }
    }
}
